import { useEffect, useId, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { analysisNavigationQuery } from '../astra/context';
import {
  Building2,
  CalendarDays,
  Download,
  Eraser,
  FlaskConical,
  LandPlot,
  LogIn,
  Menu,
  Search,
  Upload,
} from 'lucide-react';
import { Icon } from '../ui/Icon';
import { LevelTrail } from '../ui/LevelTrail';
import { Dialog } from '../ui/Dialog';
import { useShell } from './ShellContext';
import { useRouteSource } from '../data-source';
import { usePersona } from '../auth/RoleContext';
import { personaLabel, roleSpec, type Persona } from '../auth/personas';
import { useTrack } from '../data/TrackContext';
import { useFixtureData } from '../data/useTrackData';
import { DATA_ROOT, FILES, STUB_DIR, TRACK_DIR, loadJson } from '../data/loader';
import { clearOverlay, exportOverlay, importOverlay, useOverlay } from '../auth/overlay';
import type { ContourMeta, Farm, Meta, TrackId } from '../data/types';
import type { Journal } from '../astra/model';
import { localIsoDate } from '../../lib/localDate';
import { AnalysisContextChip } from '../../analysis/ContextBar';
import { useAnalysisContext } from '../../analysis/context';
import { api } from '../../api/client';
import type { IntlShape } from 'react-intl';
import { defineMessages } from '../../i18n/define';
import { UNIT_LONG, useFormat, useIntlSafe, useT } from '../../i18n';
import type { Formatters } from '../../i18n/hooks';
import { COMMON } from '../../i18n/common';
import { SHELL } from './messages';

const m = defineMessages({
  track: { id: 'shell.track.label', defaultMessage: 'Трек' },
  trackGroup: { id: 'shell.track.group', defaultMessage: 'Трек данных', description: 'Подпись группы кнопок A/B для экранного диктора' },
  contourTitle: { id: 'shell.search.contourTitle', defaultMessage: 'Контур № {no}' },
  farmFallback: { id: 'shell.search.farmFallback', defaultMessage: 'Хозяйство #{id}' },
  ownFarm: { id: 'shell.search.ownFarm', defaultMessage: 'моё хозяйство' },
  farmUnmatched: { id: 'shell.search.farmUnmatched', defaultMessage: 'хозяйство не сопоставлено' },
  areaUnknown: { id: 'shell.search.areaUnknown', defaultMessage: 'площадь не названа' },
  landUserUnknown: { id: 'shell.search.landUserUnknown', defaultMessage: 'землепользователь не назван' },
  placeholderFarmer: { id: 'shell.search.placeholderFarmer', defaultMessage: 'Номер своего контура' },
  placeholder: { id: 'shell.search.placeholder', defaultMessage: 'Номер контура или хозяйство' },
  ariaFarmer: { id: 'shell.search.ariaFarmer', defaultMessage: 'Поиск по контурам хозяйства' },
  aria: { id: 'shell.search.aria', defaultMessage: 'Поиск по срезу' },
  results: { id: 'shell.search.results', defaultMessage: 'Результаты поиска' },
  tooShort: { id: 'shell.search.tooShort', defaultMessage: 'Введите хотя бы два знака: по одному совпадений слишком много.' },
  loading: { id: 'shell.search.loading', defaultMessage: 'Реестр среза ещё читается — поиск ответит, как только данные придут.' },
  noSourceFarmer: { id: 'shell.search.noSourceFarmer', defaultMessage: 'Реестр контуров не прочитан: искать не по чему.' },
  noSource: { id: 'shell.search.noSource', defaultMessage: 'Реестр контуров и список хозяйств не прочитаны: искать не по чему.' },
  noHitsFarmer: { id: 'shell.search.noHitsFarmer', defaultMessage: 'Совпадений нет. Ищутся только контуры вашего хозяйства — чужие сюда не попадают.' },
  noHits: { id: 'shell.search.noHits', defaultMessage: 'Совпадений нет. Ищется номер контура и имя хозяйства — массивы и работы сюда не попадают.' },
  demoDownloaded: { id: 'shell.demo.downloaded', defaultMessage: 'Скачано: {file}.' },
  demoNoJournal: { id: 'shell.demo.noJournal', defaultMessage: 'Журнал набора не прочитан. Повторите загрузку после появления данных трека.' },
  demoUnknownObligation: { id: 'shell.demo.unknownObligation', defaultMessage: 'в сценарии есть обязательство, которого нет в выбранном наборе' },
  demoOutOfPeriod: { id: 'shell.demo.outOfPeriod', defaultMessage: 'дата отчёта находится вне доступного периода работы' },
  demoLoaded: { id: 'shell.demo.loaded', defaultMessage: 'Сценарий загружен: локальный слой заменён содержимым файла.' },
  demoRejected: { id: 'shell.demo.rejected', defaultMessage: 'Файл не принят: {reason}.' },
  demoUnreadable: { id: 'shell.demo.unreadable', defaultMessage: 'Файл не удалось прочитать.' },
  demoTitle: { id: 'shell.demo.title', defaultMessage: 'Демо-слой: отчёты, суждения и поручения этого браузера' },
  demoLayer: { id: 'shell.demo.layer', defaultMessage: 'Демо-слой' },
  demoCaption: { id: 'shell.demo.caption', defaultMessage: 'демо-ввод браузера: отчёты, суждения, поручения' },
  demoDownload: { id: 'shell.demo.download', defaultMessage: 'Скачать сценарий' },
  demoUpload: { id: 'shell.demo.upload', defaultMessage: 'Загрузить сценарий' },
  demoEmpty: { id: 'shell.demo.empty', defaultMessage: 'Слой уже пуст — очищать нечего' },
  demoClearTitle: { id: 'shell.demo.clearTitle', defaultMessage: 'Очистить демо-слой' },
  demoCleared: { id: 'shell.demo.cleared', defaultMessage: 'Демо-слой очищен: локальный ввод сброшен.' },
  demoClearBody: {
    id: 'shell.demo.clearBody',
    defaultMessage: 'Очистятся все отчёты, суждения и поручения, внесённые в этом браузере для выбранного набора. Действие необратимо и затрагивает только этот слой — фикстуры среза не меняются.',
  },
  guestTitle: { id: 'shell.persona.guestTitle', defaultMessage: 'Чтение района открыто без входа. Войти, чтобы получить свою роль' },
  asOfTitle: { id: 'shell.asOf.title', defaultMessage: 'Дата демонстрационного набора' },
});

const TRACKS: TrackId[] = ['A', 'B'];

/** Бюджет выпадающего списка: длиннее его уже не читают, а листают. */
const HITS_MAX = 8;
const QUERY_MIN = 2;

function TrackSwitch() {
  const { track, setTrack } = useTrack();
  const navigate = useNavigate();
  const { persona, signOut } = usePersona();
  const t = useT();
  const changeTrack = (id: TrackId) => {
    if (id === track) return;
    const query = analysisNavigationQuery(new URLSearchParams(), id);
    setTrack(id);
    // IDs belong to the selected demonstration dataset, including the farmer persona.
    if (persona?.role === 'farmer') { signOut(); navigate('/login', { replace:true }); }
    else navigate(`/${query}`, { replace:true });
  };
  return (
    <div className="tracksw">
      <span className="tracksw__label">{t(m.track)}</span>
      <div className="tracksw__group" role="group" aria-label={t(m.trackGroup)}>
        {TRACKS.map((id) => (
          <button
            key={id}
            type="button"
            className={`tracksw__btn focus-ring ${track === id ? 'is-on' : ''}`}
            aria-pressed={track === id}
            onClick={() => changeTrack(id)}
          >
            {id}
          </button>
        ))}
      </div>
    </div>
  );
}

interface Hit {
  key: string;
  to: string;
  kind: 'contour' | 'farm';
  title: string;
  note: string;
}

/**
 * Поиск по срезу: номер контура и имя хозяйства. Массивов в выдаче нет —
 * их всего 22, и они целиком лежат раскрытием на обзоре района.
 *
 * У фермера знаменатель поиска другой: только СВОИ контуры. Чужой контур
 * ему не показывается даже строкой выдачи, а хозяйства не ищутся вовсе —
 * страница хозяйства на его уровне не открывается.
 */
function collect(
  query: string,
  contours: ContourMeta[],
  farms: Farm[],
  persona: Persona | null,
  intl: IntlShape,
  format: Formatters,
): Hit[] {
  const needle = query.trim().toLocaleLowerCase('ru-RU');
  if (needle.length < QUERY_MIN) return [];

  const ownFarmId = persona?.role === 'farmer' ? (persona.farmId ?? null) : null;

  if (ownFarmId) {
    const own: Hit[] = [];
    for (const row of contours) {
      if (own.length >= HITS_MAX) break;
      if (row.farm_id !== ownFarmId) continue;
      if (!row.no.toLocaleLowerCase('ru-RU').includes(needle)) continue;
      own.push({
        key: `contour-${row.id}`,
        to: `/contours/${row.id}`,
        kind: 'contour',
        title: intl.formatMessage(m.contourTitle, { no: row.no }),
        note: `${format.unit(row.area_ha, 'ha')} · ${persona?.farmName ?? intl.formatMessage(m.ownFarm)}`,
      });
    }
    return own;
  }

  const farmNameById = new Map(farms.map((f) => [f.id, f.name]));

  const farmHits: Hit[] = [];
  for (const farm of farms) {
    if (farmHits.length >= HITS_MAX) break;
    if (!farm.name.toLocaleLowerCase('ru-RU').includes(needle)) continue;
    farmHits.push({
      key: `farm-${farm.id}`,
      to: `/farms/${farm.id}`,
      kind: 'farm',
      title: farm.name,
      note: `${farm.direction} · ${intl.formatMessage(UNIT_LONG.contours, { count: farm.contours })}`,
    });
  }

  const contourHits: Hit[] = [];
  for (const row of contours) {
    if (contourHits.length >= HITS_MAX) break;
    if (!row.no.toLocaleLowerCase('ru-RU').includes(needle)) continue;
    contourHits.push({
      key: `contour-${row.id}`,
      to: `/contours/${row.id}`,
      kind: 'contour',
      title: intl.formatMessage(m.contourTitle, { no: row.no }),
      note: `${format.unit(row.area_ha, 'ha')} · ${farmNameById.get(row.farm_id) ?? intl.formatMessage(m.farmUnmatched)}`,
    });
  }

  // Запрос с цифры почти всегда про номер контура — тогда контуры идут первыми.
  const numberFirst = /\d/.test(needle[0] ?? '');
  const ordered = numberFirst ? [...contourHits, ...farmHits] : [...farmHits, ...contourHits];
  return ordered.slice(0, HITS_MAX);
}

const MICROHA_PER_HA = 1_000_000;

/**
 * Поиск по базе: контуры по номеру и хозяйства по имени, в области
 * вошедшего (аккаунту хозяйства сервер сам сужает реестр контуров до его
 * земли). Запрос уходит после паузы в наборе, ответ на устаревший запрос
 * отбрасывается.
 */
function useServerHits(
  query: string,
  isFarmer: boolean,
): { hits: Hit[]; loading: boolean; failed: boolean } {
  const analysis = useAnalysisContext();
  const intl = useIntlSafe();
  const format = useFormat();
  const [state, setState] = useState<{ key: string; hits: Hit[]; failed: boolean }>({
    key: '',
    hits: [],
    failed: false,
  });
  const needle = query.trim();
  const key = needle.length >= QUERY_MIN ? `${analysis.key}|${isFarmer}|${needle}` : '';

  useEffect(() => {
    if (!key) return undefined;
    let alive = true;
    const timer = window.setTimeout(() => {
      const params = { ...analysis.params, search: needle, limit: HITS_MAX };
      void Promise.all([
        api.contours(params),
        isFarmer ? Promise.resolve(null) : api.actors(params),
      ])
        .then(([contourList, actorList]) => {
          if (!alive) return;
          const contourHits: Hit[] = contourList.items.map((row) => ({
            key: `contour-${row.id}`,
            to: `/contours/${row.id}`,
            kind: 'contour',
            title: intl.formatMessage(m.contourTitle, { no: row.contour_no }),
            note: `${row.total_microha === null ? intl.formatMessage(m.areaUnknown) : format.unit(row.total_microha / MICROHA_PER_HA, 'ha')} · ${row.land_user?.name ?? intl.formatMessage(m.landUserUnknown)}`,
          }));
          const farmHits: Hit[] = (actorList?.items ?? []).map((row) => ({
            key: `farm-${row.id}`,
            to: `/farms/${row.id}`,
            kind: 'farm',
            title: row.name ?? intl.formatMessage(m.farmFallback, { id: row.id }),
            note: intl.formatMessage(UNIT_LONG.contours, { count: row.contours }),
          }));
          const numberFirst = /\d/.test(needle[0] ?? '');
          const ordered = numberFirst ? [...contourHits, ...farmHits] : [...farmHits, ...contourHits];
          setState({ key, hits: ordered.slice(0, HITS_MAX), failed: false });
        })
        .catch(() => {
          if (alive) setState({ key, hits: [], failed: true });
        });
    }, 250);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
    // `analysis.params` входит в ключ через `analysis.key`; язык подписей —
    // через `intl`: строки выдачи собираются в момент ответа.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, intl.locale]);

  const fresh = state.key === key;
  return {
    hits: fresh ? state.hits : [],
    loading: key !== '' && !fresh,
    failed: fresh && state.failed,
  };
}

function SearchBox() {
  const navigate = useNavigate();
  const { persona } = usePersona();
  const isFarmer = persona?.role === 'farmer';
  const intl = useIntlSafe();
  const format = useFormat();
  const t = intl.formatMessage;
  // На каноническом маршруте поиск идёт по БАЗЕ (`/contours?search=`,
  // `/actors?search=`): идентификаторы фикстур прототипа с идентификаторами
  // канона не совпадают, и строка выдачи вела бы на чужой или несуществующий
  // контур. Фикстуры читаются только на фикстурных экранах (agroai-6al.63).
  const onApi = useRouteSource()?.source === 'api';
  const contours = useFixtureData<ContourMeta[]>(onApi ? null : FILES.contours);
  // Фермеру список хозяйств не нужен: он по нему не ищет и на страницу
  // хозяйства не ходит — лишнюю фикстуру не читаем.
  const farms = useFixtureData<Farm[]>(isFarmer || onApi ? null : FILES.farms);

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const listId = useId();

  const fixtureHits = useMemo(
    () => (onApi ? [] : collect(query, contours.data ?? [], farms.data ?? [], persona, intl, format)),
    [onApi, query, contours.data, farms.data, persona, intl, format],
  );
  const server = useServerHits(onApi ? query : '', isFarmer);
  const hits = onApi ? server.hits : fixtureHits;

  useEffect(() => {
    setCursor(0);
  }, [query]);

  // Клик мимо закрывает список: выпадашка не должна переживать переход.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const loading = onApi
    ? server.loading
    : contours.state === 'loading' || (!isFarmer && farms.state === 'loading');
  const noSource = onApi
    ? server.failed
    : isFarmer
      ? contours.state === 'missing'
      : contours.state === 'missing' && farms.state === 'missing';
  const short = query.trim().length > 0 && query.trim().length < QUERY_MIN;

  function go(hit: Hit) {
    setOpen(false);
    setQuery('');
    navigate(hit.to);
  }

  const showPanel = open && query.trim().length > 0;

  return (
    <div className="searchbox" ref={boxRef}>
      <label className="searchbox__field">
        <Icon icon={Search} size={16} />
        <input
          type="search"
          placeholder={t(isFarmer ? m.placeholderFarmer : m.placeholder)}
          aria-label={t(isFarmer ? m.ariaFarmer : m.aria)}
          role="combobox"
          aria-expanded={showPanel}
          aria-controls={listId}
          aria-autocomplete="list"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false);
              return;
            }
            if (event.key === 'ArrowDown' && hits.length > 0) {
              event.preventDefault();
              setOpen(true);
              setCursor((v) => (v + 1) % hits.length);
              return;
            }
            if (event.key === 'ArrowUp' && hits.length > 0) {
              event.preventDefault();
              setCursor((v) => (v - 1 + hits.length) % hits.length);
              return;
            }
            if (event.key === 'Enter' && hits.length > 0) {
              event.preventDefault();
              go(hits[Math.min(cursor, hits.length - 1)]!);
            }
          }}
        />
      </label>

      {showPanel ? (
        <div className="searchdrop" id={listId} role="listbox" aria-label={t(m.results)}>
          {hits.length > 0 ? (
            hits.map((hit, i) => (
              <button
                key={hit.key}
                type="button"
                role="option"
                aria-selected={i === cursor}
                className={`searchdrop__hit focus-ring ${i === cursor ? 'is-on' : ''}`}
                onMouseEnter={() => setCursor(i)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => go(hit)}
              >
                <span className="searchdrop__icon" aria-hidden="true">
                  <Icon icon={hit.kind === 'farm' ? Building2 : LandPlot} size={16} />
                </span>
                <span className="searchdrop__main">
                  <span className="searchdrop__title">{hit.title}</span>
                  <span className="searchdrop__note">{hit.note}</span>
                </span>
              </button>
            ))
          ) : (
            <div className="searchdrop__empty">
              {t(
                short
                  ? m.tooShort
                  : loading
                    ? m.loading
                    : noSource
                      ? isFarmer
                        ? m.noSourceFarmer
                        : m.noSource
                      : isFarmer
                        ? m.noHitsFarmer
                        : m.noHits,
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Меню демо-слоя: скачать текущий сессионный ввод файлом, загрузить чужой
 * сценарий взамен своего или очистить свой. Тихая кнопка — не соревнуется
 * весом с поиском или переключателем трека, но лежит рядом с ними: слой —
 * тоже часть состояния данных на экране, а не настройка приложения.
 */
/**
 * Журнал набора нужен единственному действию — проверке загружаемого
 * сценария. Читать его при монтировании топбара значит тянуть самый крупный
 * файл фикстур на каждом маршруте, включая те, где журнал не нужен вовсе.
 */
async function loadJournal(track: TrackId): Promise<Journal | null> {
  const primary = await loadJson<Journal>(`${DATA_ROOT}/${TRACK_DIR[track]}/analysis.json`);
  if (primary.ok) return primary.value;
  const fallback = await loadJson<Journal>(`${DATA_ROOT}/${STUB_DIR}/analysis.json`);
  return fallback.ok ? fallback.value : null;
}

function DemoLayerMenu() {
  const { track } = useTrack();
  const overlay = useOverlay();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const panelId = useId();

  const isEmpty =
    overlay.executions.length === 0 &&
    overlay.verifications.length === 0 &&
    overlay.campaigns.length === 0 &&
    overlay.assignments.length === 0;

  // Клик мимо закрывает панель — тот же приём, что у поиска выше.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  function downloadScenario() {
    const stamp = localIsoDate();
    const blob = new Blob([exportOverlay()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `proto-overlay-${stamp}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setStatus({ ok: true, text: t(m.demoDownloaded, { file: `proto-overlay-${stamp}.json` }) });
  }

  function onFileChosen(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    // Сброс значения сразу: повторный выбор того же файла должен снова
    // дать событие change, а не быть тихо проигнорирован браузером.
    event.target.value = '';
    if (!file) return;
    void (async () => {
      try {
        const [text, journal] = await Promise.all([file.text(), loadJournal(track)]);
        if (!journal) { setStatus({ ok: false, text: t(m.demoNoJournal) }); return; }
        const byId = new Map(journal.obligations.map(o => [o.id, o]));
        const works = new Map(journal.works.map(w => [w.id, w]));
        const result = importOverlay(text, row => {
          const obligation = byId.get(row.obligation_id), work = obligation && works.get(obligation.work_id);
          if (!work) return t(m.demoUnknownObligation);
          if (row.on < work.window.from || row.on > journal.as_of) return t(m.demoOutOfPeriod);
          return null;
        }, journal.as_of);
        setStatus(
          result.ok
            ? { ok: true, text: t(m.demoLoaded) }
            : { ok: false, text: t(m.demoRejected, { reason: result.reason ?? t(COMMON.noReason) }) },
        );
      } catch {
        setStatus({ ok: false, text: t(m.demoUnreadable) });
      }
    })();
  }

  return (
    <div className="demolayer" ref={boxRef}>
      <button
        type="button"
        className="demolayer__trigger focus-ring"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        title={t(m.demoTitle)}
      >
        <Icon icon={FlaskConical} size={16} />
        <span>{t(m.demoLayer)}</span>
      </button>

      {open ? (
        <div className="demolayer__panel" id={panelId}>
          <p className="demolayer__caption t-caption">
            {t(m.demoCaption)}
          </p>

          <button type="button" className="demolayer__item focus-ring" onClick={downloadScenario}>
            <Icon icon={Download} size={16} />
            {t(m.demoDownload)}
          </button>

          <button
            type="button"
            className="demolayer__item focus-ring"
            onClick={() => fileRef.current?.click()}
          >
            <Icon icon={Upload} size={16} />
            {t(m.demoUpload)}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json"
            className="visually-hidden"
            tabIndex={-1}
            aria-hidden="true"
            onChange={onFileChosen}
          />

          <button
            type="button"
            className="demolayer__item focus-ring"
            disabled={isEmpty}
            title={isEmpty ? t(m.demoEmpty) : undefined}
            onClick={() => setConfirmClear(true)}
          >
            <Icon icon={Eraser} size={16} />
            {t(COMMON.clear)}
          </button>

          {status ? (
            <p
              className={`demolayer__status t-caption ${status.ok ? '' : 'demolayer__status--fail'}`}
              role="status"
            >
              {status.text}
            </p>
          ) : null}
        </div>
      ) : null}

      {confirmClear ? (
        <Dialog
          title={t(m.demoClearTitle)}
          confirmLabel={t(COMMON.clear)}
          danger
          onClose={() => setConfirmClear(false)}
          onConfirm={() => {
            clearOverlay();
            setConfirmClear(false);
            setStatus({ ok: true, text: t(m.demoCleared) });
          }}
        >
          <p>{t(m.demoClearBody)}</p>
        </Dialog>
      ) : null}
    </div>
  );
}

/**
 * Чип персоны. Он только называет, чьими глазами открыт экран: сменить роль
 * можно внизу сайдбара, дублировать это действие в шапке незачем.
 */
function PersonaChip() {
  const { persona } = usePersona();
  const navigate = useNavigate();
  const t = useT();
  if (!persona) return null;

  const spec = roleSpec(persona.role);
  const isFarmer = persona.role === 'farmer';

  // Не вошедший читает район как наблюдатель, и вход обязан быть виден: до
  // этого среза он жил в шапке прежней оболочки (`SignInForm`), и потерять
  // его вместе с переносом значило бы, что войти на станции больше негде.
  if (persona.anonymous) {
    return (
      <button
        type="button"
        className="topbar__persona topbar__persona--in focus-ring"
        onClick={() => navigate('/login')}
        title={t(m.guestTitle)}
      >
        <Icon icon={LogIn} size={16} />
        <span className="topbar__personaname">{t(COMMON.signIn)}</span>
      </button>
    );
  }

  return (
    <span
      className="topbar__persona"
      title={isFarmer ? `${spec.title}: ${personaLabel(persona)}` : spec.tagline}
    >
      <Icon icon={spec.icon} size={16} />
      <span className="topbar__personaname">{personaLabel(persona)}</span>
    </span>
  );
}

export function Topbar({ navOpen = false, onMenu }: { navOpen?: boolean; onMenu?: () => void } = {}) {
  const { trail } = useShell();
  const t = useT();
  const format = useFormat();
  // Дата набора фикстур — про фикстуры, и читается из них явно: на
  // каноническом маршруте `current` вернул бы отказ, а подпись сверху
  // относится к переключателю треков, а не к срезу канона.
  const meta = useFixtureData<Meta>(FILES.meta);
  // Только прочитанная дата: подставленная константа печаталась бы как срез,
  // которого никто не читал.
  const asOf = meta.data?.as_of ?? null;
  // Переключатель наборов фикстур живёт только там, где фикстуры и есть. На
  // каноническом экране он не менял бы ничего и врал бы о происхождении
  // чисел; срез там задаёт полоса контекста (`AnalysisContextBar`).
  const fixtures = useRouteSource()?.source !== 'api';

  return (
    <header className="topbar">
      {onMenu ? (
        <button
          type="button"
          className="topbar__menu focus-ring"
          aria-label={t(navOpen ? SHELL.menuClose : SHELL.menuOpen)}
          aria-expanded={navOpen}
          aria-controls="app-nav"
          onClick={onMenu}
        >
          <Icon icon={Menu} size={20} />
        </button>
      ) : null}
      <div className="topbar__trail">
        <LevelTrail steps={trail} />
      </div>

      <div className="topbar__tools">
        <SearchBox />

        {fixtures ? <TrackSwitch /> : null}

        {/* Срез канона (`as_of`, демо) — чипом в шапке, только на
            канонических экранах: там он меняет числа, на фикстурах — нет
            (agroai-6al.38, журнал №350). */}
        {fixtures ? null : <AnalysisContextChip />}

        {/* Дата набора фикстур стоит только на фикстурных экранах: на
            каноническом маршруте срез задаёт полоса контекста, и вторая дата
            рядом — из другого мира — читалась бы как срез канона. */}
        {fixtures && asOf ? (
          <span className="topbar__asof" title={t(m.asOfTitle)}>
            <Icon icon={CalendarDays} size={16} />
            {format.date(asOf, 'short')}
          </span>
        ) : null}

        {/* Демо-слой браузера — только фикстурным экранам: на каноне записи
            идут на сервер, и меню «скачать/очистить слой» говорило бы о
            записях, которых там нет (журнал №629). */}
        {fixtures ? <DemoLayerMenu /> : null}

        <PersonaChip />
      </div>
    </header>
  );
}
